import React, { useEffect, useMemo, useState } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import { useSticky } from "react-table-sticky";
import InputField from "../../../components/InputField";
import SearchIcon from "../../../assets/SearchIcon";
import Button from "../../../components/Button";
import ManagementTableLayout from "../../../components/MangementTable";
import ShowEntries from "../../../components/ShowEntries";
import Pagination from "../../../components/Pagination";
import PlusIcon from "../../../assets/PlusIcon";
import EditIcon from "../../../assets/EditIcon";
import TrashIcon from "../../../assets/TrashIcon";
import loadingSpinner from "../../../assets/svg/spinner.svg";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Pagination2 from "../../../components/Pagination";
import Modal from "../../../components/common/Modal";
import { protectedAxiosInstance } from "../../../api/axiosManagement";
import _debounce from "lodash/debounce";
import Warning from "../../../assets/svg-tsx/Warning";
import { useSetRecoilState } from "recoil";
import { productBrandAtom } from "../../../atom";
import { checkPermissions } from "../../../utils/helper";
import { Tooltip } from "antd";

const Table = ({ setCount }: any) => {
  const [totalCount, setTotalCount] = useState<number>(100);
  const [page, setPage] = useState(1);
  const [pageLength, setPageLength] = useState<number>(25);
  const [loading, setLoading] = useState(true);
  const [inputVal, setInputVal] = useState("");
  const [brandsData, setBrandsData] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const [deleteModal, setDeleteModal] = useState<any>("");
  const setProductBrandState = useSetRecoilState(productBrandAtom);

  //Permissions
  const hasCreatePermission = checkPermissions("create_product");
  const hasEditPermission = checkPermissions("edit_product");
  const hasDeletePermission = checkPermissions("delete_product");

  const fetchBrandData = async () => {
    setLoading(true);
    protectedAxiosInstance
      .get("/admin/products/brand", {
        params: { page: page, length: pageLength, search: inputVal },
      })
      .then((res) => {
        const { brand, pagination } = res.data.data;

        setBrandsData(brand);
        setCount(pagination.total_count);
        setTotalCount(pagination.total_count);
        setLoading(false);
      })
      .catch((error: any) => {
        setLoading(false);
      });
  };

  const [time, setTime] = useState(0);
  const debouncedFetchBrandData = _debounce(fetchBrandData, time);
  useEffect(() => {
    setTime(500);
    setPage(1);
    const fetchData = async () => {
      await debouncedFetchBrandData();
    };

    fetchData();

    return () => {
      debouncedFetchBrandData.cancel();
    };
  }, [inputVal, pageLength]);

  useEffect(() => {
    setTime(0);
    const fetchData = async () => {
      await debouncedFetchBrandData();
    };
    fetchData();
    return () => {
      debouncedFetchBrandData.cancel();
    };
  }, [page]);

  const handleDeleteBrands = async (id: any) => {
    try {
      await protectedAxiosInstance.delete(`/admin/products/brand/${id}`);
      let settablePage: any;
      if ((totalCount - 1) % pageLength === 0) {
        settablePage = page - 1;
      } else {
        settablePage = page;
      }
      setPage(settablePage === 0 ? 1 : settablePage);
      setDeleteModal("");
      toast.success("Brand deleted successfully");
      setProductBrandState([]);
      fetchBrandData();
    } catch (error) {
      console.error(error);
      toast.error("Error deleting brand");
    }
  };

  const COLUMN = [
    {
      id: "sr",
      Header: () => <div className="whitespace-nowrap">Sr. No</div>,
      width: "4.5%",
      accessor: "sr",
      Cell: ({ row }: { row: any }) => (
        <p className="py-2">{startIndex + row.index + 1}</p>
      ),
    },
    {
      id: "name",
      Header: () => <div>Brands</div>,
      accessor: "name",
      Cell: ({ value }: any) => (
        <p className="py-2 tracking-[0.05rem] font-gilroy-medium">{value}</p>
      ),
    },
    ...(hasEditPermission
      ? [
          {
            id: "id",
            Header: () => <div>Action</div>,
            width: "8%",
            accessor: "id",
            Cell: ({ row }: { row: any }) => (
              <div className="flex gap-x-5 px-2 items-center">
                {hasEditPermission && (
                  <Tooltip title="Edit">
                    <Link
                      to={`/products/brands/${row.original.id}`}
                      className="cursor-pointer"
                    >
                      <EditIcon />
                    </Link>
                  </Tooltip>
                )}

                {hasDeletePermission && (
                  <Tooltip title="Delete">
                    <div
                      onClick={() => setDeleteModal(row.original.id)}
                      className="cursor-pointer"
                    >
                      <TrashIcon />
                    </div>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    const lastIndex = (page - 1) * pageLength;
    setStartIndex(lastIndex);
  }, [page, pageLength, totalCount]);

  const columns = useMemo(() => COLUMN, [brandsData]);
  const data = useMemo(() => brandsData, [brandsData]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: page, pageSize: pageLength },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / pageLength),
      useSticky,
    },
    useSortBy,
    usePagination
  );
  const handlePageChange = (page: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (size: any) => {
    setPageLength(size);
  };
  return (
    <div className="h-full">
      <div className="flex flex-row justify-between items-center py-2 w-full px-3 h-[3rem] ">
        <div className="flex gap-4 w-[max-content]">
          <div className=" w-[235px]">
            <InputField
              onChange={(e: string) => setInputVal(e)}
              placeholder="Search here"
              value={inputVal}
              rightIcon={<SearchIcon />}
            />
          </div>
          {hasCreatePermission && (
            <div className="w-fit">
              <Link to={"/products/brands/create"}>
                <Button label="Create Brand" leftIcon={<PlusIcon />} />
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="h-[calc(100%-5.5rem)] w-full relative -z-0">
        <ManagementTableLayout
          tableInstance={tableInstance}
          loading={loading}
        />
      </div>
      {loading ? (
        ""
      ) : (
        <div className="w-full">
          <Pagination2
            currentPage={page}
            pageSize={pageLength}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {deleteModal && (
        <Modal
          top
          header="Delete Brand"
          handleCancel={() => setDeleteModal("")}
          footer={
            <div className="flex gap-x-3">
              <Button
                variant="primary"
                label="Delete"
                onClick={() => handleDeleteBrands(deleteModal)}
              />
              <Button
                variant="secondary"
                label="Cancel"
                onClick={() => {
                  setDeleteModal("");
                }}
              />
            </div>
          }
        >
          <div className="flex flex-col gap-1">
            <p className="text-left">
              Are you sure, you want to delete this brand?
            </p>
            {/* <div className="flex gap-2 text-red-600 text-sm items-center">
              <Warning size="14" />
              <p>
                All the products associated with this brand will be deleted.
              </p>
            </div> */}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Table;
